import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive.js';
import {
  selectTokenByNetworkAndSymbol,
  selectTokenByNetworkAndAddress,
  selectLastUpdated,
} from '../../redux/slices/tokenSlice.ts';
import { useParams, useSearchParams } from 'react-router-dom';
import TokenHomeSkeleton from './TokenHomeSkeleton.jsx';
import TokenInformation from '../../components/TokenInformation/index.jsx';
import ScrollRestore from '../../components/ScrollRestore/index.tsx';
import { useSolana } from '../../hooks/useSolana.js';
import { useLazySearchTokensQuery } from '../../redux/api/public/tokens.js';
import { selectThemeMode } from '../../redux/slices/themeSlice.js';
import TokenChart from '../../components/TokenChart/index.jsx';
import { useGiftForm } from '../../components/GiftFormContext/index.jsx';
import { openModal } from '../../redux/slices/modals.js';

const DemoContainer = styled.div`
  width: 100%;
  padding-top: 20px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;

  @media ${PHONE_MEDIA_QUERY} {
    justify-content: flex-start;
    gap: 8px;
  }
`;

const ContentRow = styled.div`
  display: flex;
  gap: 30px;
  justify-content: flex-start;
  width: 100%;
`;

export const SwapContainer = styled(ContentRow)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  @media ${PHONE_MEDIA_QUERY} {
    > *:first-child {
      order: 2;
    }
    > *:last-child {
      order: 1;
    }
  }
`;

export const TokenSwapContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    border: unset;
    padding: 0px;
    gap: 8px;
  }
`;

export const ChartContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 16px;
  padding: 16px;
  height: fit-content;
`;

export const GiftCardContainer = styled.div`
  width: 417px;
  height: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const TokenHome = () => {
  const { address } = useParams();
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );
  const quoteToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, 'solana', address),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldShowGift = searchParams.get('showGift');
  const { initializeForm } = useGiftForm();
  const dispatch = useDispatch();

  const { refreshTokenBalances } = useSolana();
  const lastUpdated = useSelector(selectLastUpdated);
  const [searchTokens] = useLazySearchTokensQuery();

  const themeMode = useSelector(selectThemeMode);

  useEffect(() => {
    if (shouldShowGift && quoteToken?.address && usdcToken.address) {
      setTimeout(() => {
        // Dirty quick fix. Something is changing the state of the gift form after we trigger the
        // initializeForm.
        initializeForm({
          initialValues: {
            token: quoteToken.address,
            network: quoteToken.network,
            baseToken: usdcToken.address,
          },
        });
        dispatch(
          openModal({
            name: 'giftFlow',
          }),
        );
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('showGift');
        setSearchParams(newParams);
      }, 1000);
    }
  }, [shouldShowGift, usdcToken, quoteToken]);

  useEffect(() => {
    refreshTokenBalances();
  }, [refreshTokenBalances]);

  useEffect(() => {
    if (!quoteToken && lastUpdated > 0 && address) {
      searchTokens({
        addresses: [address],
      });
    }
  }, [lastUpdated, address, quoteToken, searchTokens]);

  if (!usdcToken || !quoteToken) {
    return <TokenHomeSkeleton />;
  }

  return (
    <>
      <ScrollRestore />
      <DemoContainer>
        <TokenInformation token={quoteToken} />
        <TokenChart tokenAddress={address} />
      </DemoContainer>
    </>
  );
};

export default TokenHome;
